import {
  useEffect,
  useId,
  useRef,
  type InputHTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';

import {
  PHONE_COUNTRY_OPTIONS,
  parseNationalPhoneInput,
  type SupportedPhoneCountry,
} from '@digitaltattoo/shared-utils';

export type InternationalPhoneChangeResult = {
  displayValue: string;
  e164Value: string | null;
  country: SupportedPhoneCountry;
  isValid: boolean;
};

type InternationalPhoneInputProps = {
  value: string;
  country: SupportedPhoneCountry;

  onChange: (result: InternationalPhoneChangeResult) => void;

  selectClassName?: string;
  inputClassName?: string;
  containerClassName?: string;

  error?: string;
} & Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type'
>;

export function InternationalPhoneInput({
  value,
  country,
  onChange,
  selectClassName = 'w-full p-3 border rounded-lg border-gray-300',
  inputClassName = 'w-full p-3 border rounded-lg border-gray-300',
  containerClassName = '',
  error,
  id,
  required,
  ...inputProps
}: InternationalPhoneInputProps) {
  const generatedId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const cursorFrameRef = useRef<number>();
  const inputId = id ?? `phone-${generatedId}`;
  const errorId = `${inputId}-error`;

  useEffect(() => () => {
    if (cursorFrameRef.current !== undefined) {
      window.cancelAnimationFrame(cursorFrameRef.current);
    }
  }, []);

  const restoreCursor = (displayValue: string, rawValue: string, rawCursor: number) => {
    const digitsBeforeCursor = rawValue.slice(0, rawCursor).replace(/\D/g, '').length;
    let nextCursor = 0;
    let digitsSeen = 0;

    while (nextCursor < displayValue.length && digitsSeen < digitsBeforeCursor) {
      if (/\d/.test(displayValue[nextCursor])) {
        digitsSeen += 1;
      }
      nextCursor += 1;
    }

    while (nextCursor < displayValue.length && /\D/.test(displayValue[nextCursor])) {
      nextCursor += 1;
    }

    if (cursorFrameRef.current !== undefined) {
      window.cancelAnimationFrame(cursorFrameRef.current);
    }
    cursorFrameRef.current = window.requestAnimationFrame(() => {
      if (document.activeElement === inputRef.current) {
        inputRef.current?.setSelectionRange(nextCursor, nextCursor);
      }
    });
  };

  const handleInputChange = (rawValue: string, cursor: number | null) => {
    // Champ vide
    if (!rawValue.trim()) {
      onChange({
        displayValue: '',
        e164Value: null,
        country,
        isValid: !required,
      });

      return;
    }

    const parsed = parseNationalPhoneInput(rawValue, country);
    const displayValue = parsed.displayValue;

    onChange({
      displayValue,
      e164Value: parsed.e164Value,
      country: parsed.country,
      isValid: parsed.isValid,
    });

    if (cursor !== null) {
      restoreCursor(displayValue, rawValue, cursor);
    }
  };

  const handleInputKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    inputProps.onKeyDown?.(event);

    if (
      event.defaultPrevented
      || (event.key !== 'Backspace' && event.key !== 'Delete')
      || event.altKey
      || event.ctrlKey
      || event.metaKey
      || event.currentTarget.selectionStart === null
      || event.currentTarget.selectionEnd === null
      || event.currentTarget.selectionStart !== event.currentTarget.selectionEnd
    ) {
      return;
    }

    const cursor = event.currentTarget.selectionStart;
    const adjacentIndex = event.key === 'Backspace' ? cursor - 1 : cursor;

    // Le navigateur sait deja supprimer un chiffre. On intervient uniquement
    // quand il essaierait de supprimer un espace ajoute par le formatage.
    if (adjacentIndex < 0 || /\d/.test(value[adjacentIndex] ?? '')) {
      return;
    }

    const relativeDigitIndex = event.key === 'Backspace'
      ? value.slice(0, adjacentIndex).search(/\d\D*$/)
      : value.slice(adjacentIndex).search(/\d/);
    const digitIndex = event.key === 'Delete' && relativeDigitIndex >= 0
      ? relativeDigitIndex + adjacentIndex
      : relativeDigitIndex;

    if (digitIndex < 0 || digitIndex >= value.length) {
      return;
    }

    event.preventDefault();
    const nextValue = `${value.slice(0, digitIndex)}${value.slice(digitIndex + 1)}`;
    const nextCursor = event.key === 'Backspace' ? digitIndex : cursor;
    handleInputChange(nextValue, nextCursor);
  };

  const handleCountryChange = (
    nextCountry: SupportedPhoneCountry,
  ) => {
    /*
     * Quand on change de pays, il faut reparser le numéro
     * avec le nouveau pays.
     */
    const parsed = parseNationalPhoneInput(value, nextCountry);

    onChange({
      displayValue: parsed.displayValue,
      e164Value: parsed.e164Value,
      country: nextCountry,
      isValid: parsed.isValid,
    });
  };

  return (
    <div className={containerClassName}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <select
          value={country}
          onChange={(event) =>
            handleCountryChange(
              event.target.value as SupportedPhoneCountry,
            )
          }
          className={selectClassName}
          aria-label="Pays du numéro de téléphone"
        >
          {PHONE_COUNTRY_OPTIONS.map((item) => (
            <option
              key={item.iso2}
              value={item.iso2}
            >
              {item.flag} {item.label} ({item.callingCode})
            </option>
          ))}
        </select>

        <input
          {...inputProps}
          ref={inputRef}
          id={inputId}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          required={required}
          value={value}
          onKeyDown={handleInputKeyDown}
          onChange={(event) => handleInputChange(
            event.target.value,
            event.target.selectionStart,
          )}
          className={inputClassName}
          aria-invalid={Boolean(error)}
          aria-describedby={
            error ? errorId : undefined
          }
        />
      </div>

      {error && (
        <p
          id={errorId}
          className="mt-1 text-sm text-red-600"
        >
          {error}
        </p>
      )}
    </div>
  );
}
